import { notifyForbidden } from '../lib/notifyForbidden'

export interface StarVehicle {
  vehicle_id: string
  polled_ts: string
  latitude: number | null
  longitude: number | null
  speed: number | null
  ignition_status: number | null
  soc: number | null
  soh: number | null
  voltage: number | null
  current: number | null
  cell_temperature_highest: number | null
  cell_temperature_lowest: number | null
  charging_status: number | null
}

export interface StarSummary {
  vehicles_reporting: number
  avg_soc: number | null
  stationary_now: number
  alerts_today: number
}

export type StarAlertKind =
  | 'HARSH_BRAKE' | 'HARSH_ACCEL' | 'OVERSPEED' | 'IDLING' | 'UNSCHEDULED_STOP'
  | 'BATTERY_OVERHEAT'
  | string // NETRADYNE_* is open-ended -- see tools/sync_star_netradyne.py's _kind()

export interface StarAlert {
  alert_id: string
  vehicle_id: string
  kind: StarAlertKind
  severity: 'INFO' | 'WARN' | 'CRITICAL'
  ts: string
  message: string | null
  detail: Record<string, unknown> | null
}

/** The 9 KML-derived suppression points (db/star_hub_seed.sql) -- for the map only, not
 *  part of the live idling/unscheduled-stop suppression path itself (that's server-side,
 *  serving/app.py's _load_star_hubs). */
export interface StarHub {
  name: string
  latitude: number
  longitude: number
  buffer_m: number
  kind: string | null
}

async function starFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  })
  const body = await r.json().catch(() => ({}))
  if (!r.ok) {
    const msg = (body as { error?: string }).error ?? `Request failed (${r.status})`
    if (r.status === 403) notifyForbidden(msg)
    throw new Error(msg)
  }
  return body as T
}

export async function fetchStarLive(): Promise<StarVehicle[]> {
  const data = await starFetch<{ rows?: StarVehicle[] }>('/api/star/live')
  return data.rows ?? []
}

export async function fetchStarSummary(): Promise<StarSummary> {
  return starFetch<StarSummary>('/api/star/summary')
}

export async function fetchStarAlerts(kind?: string, limit = 200): Promise<StarAlert[]> {
  const q = kind ? `?kind=${encodeURIComponent(kind)}&limit=${limit}` : `?limit=${limit}`
  const data = await starFetch<{ rows?: StarAlert[] }>(`/api/star/alerts${q}`)
  return data.rows ?? []
}

export async function fetchStarHubs(): Promise<StarHub[]> {
  const data = await starFetch<{ rows?: StarHub[] }>('/api/star/hubs')
  return data.rows ?? []
}

/** One real OSM road/way within the plant+mine site -- db/schema_star.sql's
 *  star_road_geometry, populated by tools/fetch_star_road_geometry.py. NOT the H3
 *  empirical speed grid (star_speed_profile) -- that stays a backend-only signal for
 *  classify_overspeed; drawing it on the map read as scattered confetti at STAR's scale,
 *  so the console draws real road geometry instead. */
export interface StarRoad {
  way_id: number
  highway_class: string | null
  name: string | null
  /** [[lng,lat], ...] -- GeoJSON coordinate order, already Overpass "out geom" order. */
  coordinates: [number, number][]
}

export async function fetchStarRoads(): Promise<StarRoad[]> {
  const data = await starFetch<{ rows?: StarRoad[] }>('/api/star/roads')
  return data.rows ?? []
}

/** Mirrors busOps/useMbmtApi.ts's MbmtBreakdown -- same site_breakdown-backed shape,
 *  served from serving/app.py's star_breakdowns() rather than mbmt.py's router (STAR's
 *  positions live in Postgres, not the in-memory vehicles dict mbmt.py reads). */
export interface StarBreakdown {
  breakdown_id: number
  vehicle_id: string
  category: string
  severity: string
  note: string | null
  contact: string | null
  by_whom: string | null
  opened_ts: number | null
  cleared_ts: number | null
  eta_min: number | null
  lat: number | null
  lon: number | null
  /** Telemetry's own suggestion -- a poll against star_vehicle_telemetry, not a live Kafka
   *  frame the way Ultratech's is, but the same contract: a prompt for a human to confirm
   *  ("started moving at HH:MM -- resolve?"), never a silent auto-clear. See
   *  RESUMED_MOVING_POLL_SEC's own comment in serving/app.py. */
  resumed_moving_ts: number | null
  resumed_moving_lat: number | null
  resumed_moving_lon: number | null
}

export interface StarBreakdownAction {
  action: string
  by: string | null
  phone: string | null
  note: string | null
  ts: number | null
}

export interface StarBreakdownCode {
  code: string
  name: string
}

export async function fetchStarBreakdowns(openOnly = true): Promise<StarBreakdown[]> {
  const q = openOnly ? '' : '?open_only=false'
  const data = await starFetch<{ breakdowns: StarBreakdown[] }>(`/api/star/breakdowns${q}`)
  return data.breakdowns ?? []
}

export async function fetchStarBreakdownCodes(): Promise<StarBreakdownCode[]> {
  const data = await starFetch<{ codes: StarBreakdownCode[] }>('/api/star/breakdowns/codes')
  return data.codes ?? []
}

export async function openStarBreakdown(body: {
  vehicle_id: string
  category?: string
  severity?: string
  note?: string
  contact?: string
  by?: string
  phone?: string
  eta_min?: number | null
  lat?: number | null
  lon?: number | null
}): Promise<{ breakdown_id: number; reused?: boolean }> {
  return starFetch('/api/star/breakdowns', { method: 'POST', body: JSON.stringify(body) })
}

export async function actOnStarBreakdown(
  id: number,
  action: string,
  extra: { by?: string; phone?: string; note?: string } = {},
): Promise<void> {
  await starFetch(`/api/star/breakdowns/${id}/actions`, {
    method: 'POST',
    body: JSON.stringify({ action, ...extra }),
  })
}

export async function fetchStarBreakdownActionLog(id: number): Promise<StarBreakdownAction[]> {
  const data = await starFetch<{ actions: StarBreakdownAction[] }>(`/api/star/breakdowns/${id}/actions`)
  return data.actions ?? []
}

/** Clears the "resumed moving" suggestion without resolving anything -- for the
 *  false-positive case (a push, a shove to check the brakes) and nothing since. Same
 *  action name and semantics as Ultratech's own dismiss-moving. */
export async function dismissStarMoving(id: number): Promise<void> {
  await starFetch(`/api/star/breakdowns/${id}/actions`, {
    method: 'POST',
    body: JSON.stringify({ action: 'dismiss-moving' }),
  })
}

// ---------------------------------------------------------------- day-wise breakdown reports
export type StarBreakdownReportKind = 'daily' | 'downtime' | 'repeat' | 'open' | 'hygiene'

export interface StarBreakdownDailyRow {
  site: string; day: string; opened: number; cleared: number; updated: number
  vehicles_down: number; immobilised: number; major: number; minor: number
}

export interface StarBreakdownDowntimeRow {
  site: string; category: string; severity: string; incidents: number; still_open: number
  vehicles: number; median_hours: number | null; p90_hours: number | null
  total_hours_lost: number | null
}

export interface StarBreakdownRepeatRow {
  site: string; vehicle_id: string; depot: string | null; incidents: number
  distinct_causes: number; most_common_cause: string | null; hours_lost: number | null
  first_incident: number; last_incident: number
}

export interface StarBreakdownOpenRow {
  site: string; breakdown_id: number; vehicle_id: string; depot: string | null
  category: string; severity: string; note: string | null; contact: string | null
  by_whom: string | null; eta_min: number | null; opened_ts: number; hours_down: number
  eta_overdue: boolean; resumed_moving_ts: number | null
}

export interface StarBreakdownHygieneRow {
  site: string; incidents_logged: number; never_cleared: number; open_over_7_days: number
  never_cleared_pct: number | null; currently_marked_down: number
}

export type StarBreakdownReportRow<K extends StarBreakdownReportKind> =
  K extends 'daily' ? StarBreakdownDailyRow :
  K extends 'downtime' ? StarBreakdownDowntimeRow :
  K extends 'repeat' ? StarBreakdownRepeatRow :
  K extends 'open' ? StarBreakdownOpenRow :
  StarBreakdownHygieneRow

export async function fetchStarBreakdownReport<K extends StarBreakdownReportKind>(
  kind: K,
): Promise<StarBreakdownReportRow<K>[]> {
  const data = await starFetch<{ rows?: StarBreakdownReportRow<K>[] }>(
    `/api/star/breakdowns/reports/${kind}`)
  return data.rows ?? []
}
